import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { SyncClient } from '@machs/shared';
import { ensureUrlToken, eventsUrl, refreshUrlToken, transport, type Me } from './api';
import { createWebStorage } from './storage';

interface SyncCtx {
  client: SyncClient;
  me: Me;
}

const Ctx = createContext<SyncCtx | null>(null);

export function SyncProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [client, setClient] = useState<SyncClient | null>(null);

  useEffect(() => {
    let cancelled = false;
    let events: EventSource | null = null;
    let c: SyncClient | null = null;

    const online = () => c?.setOnline(true);
    const offline = () => c?.setOnline(false);
    const visible = () => {
      if (document.visibilityState !== 'visible') return;
      ensureUrlToken();
      void c?.flush();
    };

    (async () => {
      const storage = await createWebStorage(me.user.id, me.tenant.id);
      c = new SyncClient(storage, transport, { userId: me.user.id, userName: me.user.name });
      await c.init();
      if (cancelled) return;
      c.setOnline(navigator.onLine);
      setClient(c);
      void c.flush();

      // Zugang kommt aus dem HttpOnly-Lese-Cookie (Gast-Ansicht: kurzlebiges
      // Lese-Token), nie aus dem Anmelde-Token
      events = new EventSource(eventsUrl());
      events.addEventListener('changes', (e) => c?.notifyRemoteCursor(Number((e as MessageEvent).data)));
      events.addEventListener('open', () => void c?.flush());
      // Abbruch kann am abgelaufenen Lese-Cookie liegen: erneuern, EventSource
      // verbindet danach von selbst wieder
      events.addEventListener('error', () => void refreshUrlToken());
    })();

    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visible);
    // Rückfallebene, falls Live-Hinweise ausbleiben
    const timer = setInterval(() => {
      ensureUrlToken();
      void c?.flush();
    }, 60_000);

    return () => {
      cancelled = true;
      events?.close();
      clearInterval(timer);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [me.user.id, me.tenant.id, me.user.name]);

  if (!client) return <div className="boot"><div className="spinner" /></div>;
  return <Ctx.Provider value={{ client, me }}>{children}</Ctx.Provider>;
}

/** Liefert Client und Benutzer; rendert bei jeder Datenänderung neu. */
export function useSync(): SyncCtx & { version: number } {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSync außerhalb von SyncProvider');
  const version = useSyncExternalStore(
    (fn) => ctx.client.subscribe(fn),
    () => ctx.client.version,
  );
  return { ...ctx, version };
}
